import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "react-modal";
import debounce from "lodash/debounce";
import History from "./utils/history";
import { LeftKeyContext } from "./context/LeftToolBarKeyContext/LeftToolbarKeyContextProvider";
import EncryptedStorage from 'core/utils/constants/encryptedStorage';


Modal.setAppElement("#root"); // Set the root element for accessibility

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        width: '50rem',
        minHeight: '50vh',
        borderRadius: '1em',
        boxShadow: '0px 16px 32px 4px rgba(0, 0, 0, 0.15)',
        padding: '20px',
        border: '0px',
        zIndex: '1600',
    },
    overlay: {
        background: 'rgba(0, 0, 0, 0.4)',
        zIndex: '1600',
    },
};

interface SubModule {
    name: string;
    route: string;
    url: string;
}

interface Module {
    name: string;
    module: string;
    path: string;
    subModules: SubModule[];
}

interface GlobalModuleSearchProps {
    arrayOfObj: Module[];
}

const GlobalModuleSearch: React.FC<GlobalModuleSearchProps> = ({ arrayOfObj }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredModules, setFilteredModules] = useState<Module[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    // Create a ref to store the original modules array
    const originalModulesRef = useRef<Module[]>([]);
    
    // Use the context directly, if it's available
    const leftKeyContext = React.useContext(LeftKeyContext);

    useEffect(() => {
        // Store the original modules array
        originalModulesRef.current = arrayOfObj;
    }, [arrayOfObj]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.key.toLowerCase() === "k") {
                event.preventDefault(); 
                setIsOpen(true);
            } else if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            setFilteredModules(arrayOfObj);
            setTimeout(() => {
                inputRef.current?.focus(); // Focus on input when modal opens
            }, 0);
        }
        return () => {
            setSearchTerm("");
            setFilteredModules([]);
        };
    }, [isOpen, arrayOfObj]);

    const filterModules = useCallback(
        debounce((query: string) => {
            if (query.trim() === "") {
                setFilteredModules(arrayOfObj);
                return;
            }

            const filtered = arrayOfObj
                .map(module => {
                    const filteredSubModules = module.subModules.filter(sub =>
                        sub.name.toLowerCase().includes(query.toLowerCase())
                    );

                    if (module.name.toLowerCase().includes(query.toLowerCase()) || filteredSubModules.length > 0) {
                        return { ...module, subModules: filteredSubModules };
                    }
                    return null;
                })
                .filter(Boolean) as Module[]; // Cast to Module[] after filtering

            setFilteredModules(filtered);
        }, 300),
        [arrayOfObj]
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        filterModules(e.target.value);
    };

    useEffect(() => {
        return () => {
            filterModules.cancel(); // Cleanup debounce on unmount
        };
    }, [filterModules]);

    const renderLink = (sub: Module | SubModule, module?: Module) => {
        let path = '';
        let basePath = '';
        
        if (module) {
            // If module is provided, sub is a SubModule
            basePath = module.path.split("/")[1];
            path = `/${basePath}/${(sub as SubModule).route}/${(sub as SubModule).url}/list`;
            
            // Navigate to the new path
            History.navigate(path);
        } else {
            // If module is not provided, sub is a Module
            const moduleItem = sub as Module;
            basePath = moduleItem.path.split("/")[1];
            
            // Find the original module from the originalModulesRef
            const originalModule = originalModulesRef.current.find(m => m.module === moduleItem.module);
            
            if (!originalModule || !originalModule.subModules?.[0]?.route) {
                path = `/${basePath}`;
            } else {
                // Use the original module's first submodule for navigation
                path = `/${basePath}/${originalModule.subModules[0].route}/${originalModule.subModules[0].url}/list`;
            }
            
            // Navigate to the new path
            History.navigate(path);
        }
        
        // Update the leftKey using EncryptedStorage as a way to communicate with the LeftToolbar
        // This is a fallback method if direct context update isn't working
        EncryptedStorage.setItem('ACTIVE_TOOLBAR_KEY', basePath);
        
        // Remove any pagination data from EncryptedStorage to start fresh
        EncryptedStorage.removeItem('PAGINATION');
        
        // Close the modal
        setIsOpen(false);
    };

    return (
        <div>
            <Modal
                isOpen={isOpen}
                onRequestClose={() => setIsOpen(false)}
                style={customStyles}
            >
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search for modules ..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    style={{
                        width: "100%",
                        padding: "10px",
                        fontSize: "16px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        marginBottom: "15px",
                    }}
                />
                <div className="min-h-[40vh] max-h-[40vh] overflow-auto w-full p-2">
                    {filteredModules.map((module) => (
                        <div key={module.module} className="w-full mb-5 border border-solid rounded-lg p-2">
                            <strong 
                                onClick={() => renderLink(module)} 
                                className="text-xl p-2 hover:text-[blue] cursor-pointer w-full"
                            >
                                {module.name}
                            </strong>
                            {module.subModules?.[0]?.name !== "Allowed" && (
                                <ul>
                                    {module.subModules.map((sub) => (
                                        <li 
                                            onClick={() => renderLink(sub, module)} 
                                            className="text-lg p-2 hover:text-[blue] cursor-pointer" 
                                            key={sub.route}
                                        >
                                            {sub.name}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            </Modal>
        </div>
    );
};

export default GlobalModuleSearch;